/**
 * §P.1 fase 2 · Los papeles de un alumno: seguros, autorizaciones y licencias.
 *
 * ## Lo que aporta es que un seguro vencido se vea sin restar fechas
 *
 * `expiring_soon` y `expired` **los calcula el servidor** y aquí se leen tal cual:
 * restarlos en la pantalla sería el segundo sitio que un día discrepa. Lo único que
 * decide esta pantalla es que «sin fecha de caducidad» no se pinta en verde, por lo
 * mismo que en los papeles del personal: de un papel del que no sabemos cuándo vence
 * no sabemos nada.
 *
 * ## Retirar no borra el fichero
 *
 * Borra la fila; el PDF se queda en el almacén hasta que el purgado de retención se
 * lo lleve. El contrato da la razón, y es buena: «un documento retirado por error se
 * recupera, y uno borrado de verdad no».
 *
 * ## El PDF no se sube todavía
 *
 * `POST /documents` **no recibe bytes**: recibe el `asset_id` de un fichero ya subido
 * por `/uploads` (§S.3), y eso es lo que permite comprobar en un solo sitio la regla
 * de que nunca se guarda una foto ni un vídeo de una persona. El panel no tiene esa
 * subida cableada, así que se anota el papel con su fecha y se dice que falta el PDF.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  CLASES_DE_DOCUMENTO_DE_ALUMNO,
  ESTADOS_DE_PAPEL,
  estadoDePapel,
  etiquetaDeDocumentoDeAlumno,
  type ClaseDeDocumentoDeAlumno,
  type DocumentoDeAlumno,
} from '@/club/alumnos/administracion/tipos';

export function PapelesDelAlumno({
  club,
  alumnoId,
  puedeEditar,
}: {
  club: string;
  alumnoId: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [anotando, setAnotando] = useState(false);
  const [clase, setClase] = useState<ClaseDeDocumentoDeAlumno>('seguro');
  const [caduca, setCaduca] = useState('');
  const [referencia, setReferencia] = useState('');
  const [retirando, setRetirando] = useState<DocumentoDeAlumno | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const clave = ['papeles-de-alumno', club, alumnoId] as const;

  const papeles = useQuery({
    queryKey: clave,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .documents.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DocumentoDeAlumno[];
    },
  });

  const anotar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .documents.post({
          kind: clase,
          expires_on: caduca === '' ? null : caduca,
          reference: referencia.trim() === '' ? null : referencia.trim(),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setAnotando(false);
      setCaduca('');
      setReferencia('');
      void cache.invalidateQueries({ queryKey: clave });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        .students({ studentId: alumnoId })
        .documents({ documentId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setRetirando(null);
      void cache.invalidateQueries({ queryKey: clave });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (retirando) {
    return (
      <div className="hueco hueco-confirmar">
        <h3>
          ¿Retirar el{' '}
          {etiquetaDeDocumentoDeAlumno(retirando.kind).toLowerCase()}?
        </h3>
        <p>
          Desaparece de la ficha. <strong>El PDF no se borra</strong>: se queda
          guardado hasta que el plazo de conservación se lo lleve, así que un
          papel retirado por error se puede recuperar.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            disabled={retirar.isPending}
            onClick={() => retirar.mutate(retirando.id)}
          >
            {retirar.isPending ? 'Retirando…' : 'Sí, retirarlo'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setRetirando(null);
              setProblema(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </div>
    );
  }

  if (anotando) {
    const elegida = CLASES_DE_DOCUMENTO_DE_ALUMNO.find(
      (c) => c.valor === clase
    );

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          anotar.mutate();
        }}
      >
        <div className="rejilla">
          <label>
            Qué papel es
            <select
              value={clase}
              onChange={(e) =>
                setClase(e.target.value as ClaseDeDocumentoDeAlumno)
              }
            >
              {CLASES_DE_DOCUMENTO_DE_ALUMNO.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cuándo caduca
            <input
              type="date"
              value={caduca}
              onChange={(e) => setCaduca(e.target.value)}
            />
          </label>

          <label>
            Número o referencia
            <input
              maxLength={120}
              placeholder="Opcional"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
        </div>

        {/* Qué es cada cosa, en el momento de elegirla. */}
        {elegida && <p className="tenue">{elegida.explicacion}</p>}

        <p className="tenue">
          El PDF todavía no se puede adjuntar desde aquí. La fecha es lo que
          hace que un seguro vencido se vea antes de una competición.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={anotar.isPending}>
            {anotar.isPending ? 'Guardando…' : 'Anotar el papel'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={anotar.isPending}
            onClick={() => {
              setAnotando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  if (papeles.isPending) {
    return <Cargando filas={2} etiqueta="Cargando los papeles" />;
  }

  if (papeles.isError || !papeles.data) {
    return (
      <PanelError
        problema={aProblema(papeles.error)}
        reintentar={() => void papeles.refetch()}
      />
    );
  }

  return (
    <div>
      {puedeEditar && (
        <div className="tutor-acciones" style={{ marginBlockStart: 0 }}>
          <button type="button" onClick={() => setAnotando(true)}>
            Anotar un papel
          </button>
        </div>
      )}

      {problema && <PanelError problema={problema} />}

      {papeles.data.length === 0 ? (
        <p className="tenue">
          No hay papeles anotados. El seguro y la licencia son los que más falta
          hacen: anotados con su fecha, se ve si están vencidos antes de una
          competición y no el día de.
        </p>
      ) : (
        <ul className="papeles">
          {papeles.data.map((d) => {
            const pinta = ESTADOS_DE_PAPEL[estadoDePapel(d)];

            return (
              <li key={d.id} className={d.expired ? 'fila-fuera' : undefined}>
                <div className="aviso-cabeza">
                  <strong>{etiquetaDeDocumentoDeAlumno(d.kind)}</strong>
                  <span className={`pastilla ${pinta.clase}`}>
                    {pinta.etiqueta}
                  </span>
                  {d.verified_at && (
                    <span className="pastilla pastilla-info">Comprobado</span>
                  )}
                </div>

                <p className="tenue">
                  {d.expires_on
                    ? `Caduca el ${fecha(d.expires_on)}`
                    : 'Sin fecha de caducidad'}
                  {d.reference ? ` · nº ${d.reference}` : ''}
                  {d.asset_id ? '' : ' · sin PDF adjunto'}
                </p>

                {puedeEditar && (
                  <div className="aviso-pie">
                    <span />
                    <button
                      type="button"
                      className="btn-plano"
                      onClick={() => {
                        setRetirando(d);
                        setProblema(null);
                      }}
                    >
                      Retirar
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
